import React from "react";
import { Box, Slider } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { RISK_COLORS, TEXT_COLORS } from "../../constants/colors";

interface SliderConfig {
  min: number;
  max: number;
  thresholds: number[];
  marks: { value: number; label: string }[];
  reversed?: boolean;
}

type MapRecord<Type extends Record<string, Value>, Value> = { [Property in keyof Type]: Value };

function mapSliderConfigType<Type extends Record<string, SliderConfig>>(input: Type): MapRecord<Type, SliderConfig> {
  return input;
}

const SLIDER_CONFIGS = mapSliderConfigType({
  normal: {
    min: 0,
    max: 100,
    thresholds: [33.3, 66.6],
    marks: [
      { value: 33.3, label: "33" },
      { value: 66.6, label: "66" },
    ],
  },
  ipi: {
    min: 1,
    max: 10,
    thresholds: [4, 7],
    marks: [
      { value: 4, label: "4" },
      { value: 7, label: "7" },
    ],
  },
  idat: {
    min: 0,
    max: 100,
    thresholds: [25, 50],
    marks: [
      { value: 25, label: "25" },
      { value: 50, label: "50" },
    ],
    reversed: true,
  },
});

interface Data {
  sliderType: keyof typeof SLIDER_CONFIGS;
  data: number;
}

const getThumbColor = (value: number, config: SliderConfig, theme: Theme) => {
  const [lowThreshold, highThreshold] = config.thresholds;

  if (config.reversed) {
    if (value >= highThreshold) return RISK_COLORS.HIGH;
    if (value >= lowThreshold) return RISK_COLORS.MEDIUM;
    return RISK_COLORS.LOW;
  } else {
    if (value > highThreshold) return theme.palette.info.main;
    if (value > lowThreshold) return RISK_COLORS.MEDIUM;
    return theme.palette.warning.main;
  }
};

const getBackgroundGradient = (config: SliderConfig) => {
  const range = config.max - config.min;
  const toPercent = (value: number) => (value / range) * 100;

  const lowPercent = toPercent(config.thresholds[0] - config.min);
  const highPercent = toPercent(config.thresholds[1] - config.min);

  if (config.reversed) {
    const redToYellowPercent = toPercent(config.max - config.thresholds[1]);
    const yellowToGreenPercent = toPercent(config.max - config.thresholds[0]);
    return `linear-gradient(to right, ${RISK_COLORS.HIGH} ${redToYellowPercent}%, ${RISK_COLORS.MEDIUM} ${redToYellowPercent}%, ${RISK_COLORS.MEDIUM} ${yellowToGreenPercent}%, ${RISK_COLORS.LOW} ${yellowToGreenPercent}%)`;
  } else {
    return `linear-gradient(to right, ${RISK_COLORS.HIGH} ${lowPercent}%, ${RISK_COLORS.MEDIUM} ${lowPercent}%, ${RISK_COLORS.MEDIUM} ${highPercent}%, ${RISK_COLORS.LOW} ${highPercent}%)`;
  }
};

export default function RiskSlider({ sliderType, data }: Data) {
  const theme = useTheme();
  const config = SLIDER_CONFIGS[sliderType] || SLIDER_CONFIGS.normal;

  const thumbColor = getThumbColor(data, config, theme);
  const backgroundColor = getBackgroundGradient(config);

  const displayValue = config.reversed ? config.max - data : data;
  const displayMarks = config.reversed
    ? config.marks.map(mark => ({ ...mark, value: config.max - mark.value }))
    : config.marks;

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="10px">
      <Box sx={{ width: "24px", textAlign: "center" }}>{config.reversed ? config.max : config.min}</Box>
      <Slider
        disabled
        sx={{
          cursor: "pointer",
          color: "blue",
          height: 12,
          width: "200px",
          marginBottom: "0px",
          "& .MuiSlider-rail": {
            backgroundImage: backgroundColor,
            opacity: 1,
          },
          "& .MuiSlider-thumb": {
            height: 20,
            width: 20,
            backgroundColor: thumbColor,
            border: "1px solid black",
          },
          "& .MuiSlider-valueLabel": {
            fontFamily: "Roboto",
            fontSize: 14,
            fontWeight: "400",
            top: 0,
            color: TEXT_COLORS.PRIMARY,
            backgroundColor: "unset",
            "&:before": {
              display: "none",
            },
          },
          "& .MuiSlider-mark": {
            backgroundColor: "#000",
            height: "12px",
            width: "2px",
          },
          "& .MuiSlider-markLabel": {
            fontFamily: "Roboto",
            fontSize: 14,
            color: TEXT_COLORS.PRIMARY,
          },
        }}
        valueLabelFormat={() => data}
        value={displayValue}
        track={false}
        min={config.min}
        max={config.max}
        marks={displayMarks}
        valueLabelDisplay="on"
      />
      <Box sx={{ width: "24px", textAlign: "center" }}>{config.reversed ? config.min : config.max}</Box>
    </Box>
  );
}
